<template>
  <div class="scenic-tool">
    <!-- 第一行：选择人像 -->
    <el-card class="panel-card" shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-camera"></i> 选择人像</span>
        <span v-if="personUrl" class="tip-text">透明人像已就绪，可继续选择背景</span>
      </div>

      <div class="gen-layout">
        <!-- 左：上传与抠图 -->
        <div class="person-pane">
          <template v-if="!personUrl">
            <el-upload
              class="upload-box"
              drag
              action="#"
              accept="image/jpeg,image/png,image/webp"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="onPersonUpload"
            >
              <img v-if="personRawPreview" :src="personRawPreview" class="person-raw-preview" alt="人像原图预览" />
              <template v-else>
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">上传人像照片</div>
                <div class="el-upload__tip">支持 jpg / png / webp</div>
              </template>
            </el-upload>
            <el-button type="primary" :loading="matting" :disabled="!personRawFile" class="full-btn" @click="doMatting">
              {{ matting ? '抠图中...' : '抠图生成人像' }}
            </el-button>
          </template>
          <template v-else>
            <el-button type="warning" plain class="full-btn" @click="resetPerson">
              <i class="el-icon-refresh-left"></i> 重新选择人像
            </el-button>
            <div class="tip-text form-tip">透明人像已就绪，可继续选择背景</div>
          </template>
        </div>

        <!-- 右：人像预览 -->
        <div class="person-show">
          <img v-if="personUrl" :src="personUrl" class="person-preview" alt="抠图人像预览" />
          <div v-else class="person-empty">
            <i class="el-icon-user"></i>
            <p>上传人像并点击「抠图生成人像」后，透明人像将显示在这里</p>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 第二行：选择背景 -->
    <el-card class="panel-card" shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-picture-outline"></i> 选择背景</span>
        <span v-if="selectedScenicId != null || backgroundFile" class="tip-text">已选择背景</span>
      </div>

      <el-radio-group v-model="bgMode" size="small" class="bg-mode">
        <el-radio-button :label="'scenic'">站点模板</el-radio-button>
        <el-radio-button :label="'custom'">自定义上传</el-radio-button>
      </el-radio-group>

      <div v-if="bgMode === 'custom'" class="custom-bg">
        <el-upload
          action="#"
          accept="image/jpeg,image/png,image/webp"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="onBgUpload"
        >
          <el-button type="primary" plain icon="el-icon-upload2">点击上传背景图</el-button>
        </el-upload>
        <div class="tip-text form-tip">支持 jpg / png / webp</div>
      </div>

      <div v-else-if="scenicLoading" class="tip-text">正在加载模板...</div>
      <div v-else-if="scenicList.length === 0" class="tip-text">暂无可用模板</div>
      <div v-else class="scenic-grid">
        <div
          v-for="item in scenicList"
          :key="item.id"
          class="scenic-item"
          :class="{ active: selectedScenicId === item.id }"
          @click="selectScenic(item)"
        >
          <img :src="fullImageUrl(item.imageUrl)" :alt="item.name" />
          <div class="scenic-name">{{ item.name }}</div>
          <i v-if="selectedScenicId === item.id" class="el-icon-check scenic-check"></i>
        </div>
      </div>
    </el-card>

    <!-- 第三行：编辑与合成 -->
    <el-card class="panel-card editor-card" shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-edit-outline"></i> 编辑并合成</span>
        <div class="header-actions">
          <span class="tip-text">拖动人像调整位置，滚轮/滑块缩放</span>
        </div>
      </div>

      <div class="canvas-box">
        <canvas ref="editor" :width="canvasW" :height="canvasH" @mousedown="onMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp" @wheel.prevent="onWheel" />
        <div v-if="!ready" class="canvas-empty">请先选择人像与背景</div>
      </div>

      <div class="scale-row">
        <span class="tip-text">缩放</span>
        <el-slider v-model="scaleVal" :min="0.1" :max="3" :step="0.01" class="scale-slider" @input="requestRender" />
      </div>

      <div class="actions">
        <el-button type="primary" :loading="compositing" :disabled="!canComposite" class="full-btn" @click="doComposite">
          {{ compositing ? '合成中...' : '合成照片并下载' }}
        </el-button>
      </div>

      <div v-if="resultUrl" class="result-box">
        <img :src="resultUrl" class="result-preview" alt="合成结果" />
        <el-button type="success" class="full-btn" @click="download">
          <i class="el-icon-download"></i> 保存照片
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import request from '@/plugins/axios'
import { matting, getScenicList, scenicComposite, blobToUrl, downloadUrl, revokeUrl } from '@/api/photo'

const BOX_WIDTH = 640

export default {
  name: 'ScenicComposite',
  data() {
    return {
      baseURL: request.defaults.baseURL,
      personRawFile: null,
      personRawPreview: '',
      matting: false,
      personBlob: null,
      personFile: null,
      personUrl: '',
      personImage: null,
      personNW: 0,
      personNH: 0,
      bgMode: 'scenic',
      scenicLoading: false,
      scenicList: [],
      selectedScenicId: null,
      selectedScenicUrl: '',
      backgroundFile: null,
      backgroundUrl: '',
      bgImage: null,
      bgNaturalW: 0,
      bgNaturalH: 0,
      dispScale: 1,
      canvasW: BOX_WIDTH,
      canvasH: 400,
      scaleVal: 1,
      centerX: BOX_WIDTH / 2,
      centerY: 200,
      dragging: false,
      ready: false,
      compositing: false,
      resultUrl: ''
    }
  },
  computed: {
    canComposite() {
      return this.ready && !!this.personFile && (!!this.backgroundFile || this.selectedScenicId != null)
    }
  },
  watch: {
    bgMode() {
      this.ready = false
      this.resultUrl = ''
    }
  },
  created() {
    this.loadScenicList()
  },
  methods: {
    async loadScenicList() {
      this.scenicLoading = true
      try {
        const res = await getScenicList()
        if (res && res.code === 200) {
          this.scenicList = res.data || []
        }
      } catch (e) {
        this.handleError(e)
      } finally {
        this.scenicLoading = false
      }
    },
    fullImageUrl(url) {
      if (!url) return ''
      if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) return url
      return this.baseURL + url.replace(/^\//, '')
    },
    onPersonUpload(file) {
      this.personRawFile = file.raw || file
      if (this.personRawPreview) revokeUrl(this.personRawPreview)
      this.personRawPreview = blobToUrl(this.personRawFile)
    },
    async doMatting() {
      if (!this.personRawFile) {
        this.msgError('请先上传人像照片')
        return
      }
      this.matting = true
      try {
        const blob = await matting(this.personRawFile)
        this.personBlob = blob
        this.personFile = new File([blob], 'person.png', { type: 'image/png' })
        if (this.personUrl) revokeUrl(this.personUrl)
        this.personUrl = blobToUrl(blob)
        this.initPerson({ fromUrl: this.personUrl })
        this.tryRender()
        this.msgSuccess('抠图成功，可移动调整')
      } catch (e) {
        this.handleError(e)
      } finally {
        this.matting = false
      }
    },
    resetPerson() {
      if (this.personUrl) revokeUrl(this.personUrl)
      if (this.personRawPreview) revokeUrl(this.personRawPreview)
      this.personRawFile = null
      this.personRawPreview = ''
      this.personFile = null
      this.personUrl = ''
      this.personImage = null
      this.ready = false
      this.resultUrl = ''
    },
    onBgUpload(file) {
      this.backgroundFile = file.raw || file
      if (this.backgroundUrl) revokeUrl(this.backgroundUrl)
      this.backgroundUrl = blobToUrl(this.backgroundFile)
      this.selectedScenicId = null
      this.selectedScenicUrl = ''
      this.initBgFromUrl(this.backgroundUrl)
    },
    selectScenic(item) {
      this.selectedScenicId = item.id
      this.selectedScenicUrl = this.fullImageUrl(item.imageUrl)
      this.backgroundFile = null
      if (this.backgroundUrl) revokeUrl(this.backgroundUrl)
      this.backgroundUrl = ''
      this.initBgFromUrl(this.selectedScenicUrl)
    },
    initBgFromUrl(url) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        this.bgImage = img
        this.bgNaturalW = img.naturalWidth
        this.bgNaturalH = img.naturalHeight
        this.dispScale = BOX_WIDTH / this.bgNaturalW
        this.canvasH = Math.round(this.bgNaturalH * this.dispScale)
        this.$nextTick(() => {
          this.centerX = this.canvasW / 2
          this.centerY = this.canvasH > 0 ? this.canvasH * 0.6 : 200
          this.baseScale()
          this.ready = !!this.personImage && !!this.bgImage
          this.tryRender()
        })
      }
      img.src = url
    },
    initPerson() {
      const img = new Image()
      img.onload = () => {
        this.personImage = img
        this.personNW = img.naturalWidth
        this.personNH = img.naturalHeight
        this.centerY = this.canvasH > 0 ? this.canvasH * 0.6 : 200
        this.baseScale()
        this.ready = !!this.personImage && !!this.bgImage
        this.tryRender()
      }
      img.src = this.personUrl
    },
    baseScale() {
      if (!this.personNH || !this.canvasH) return
      // 初始缩放：让人像高度约为画布的 55%
      const target = this.canvasH * 0.55
      const base = target / (this.personNH * this.dispScale)
      this.scaleVal = Math.max(0.1, Math.min(3, base))
      this.requestRender()
    },
    requestRender() {
      if (this.ready) {
        this.$nextTick(this.tryRender)
      }
    },
    tryRender() {
      if (!this.bgImage || !this.personImage) return
      const canvas = this.$refs.editor
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, this.canvasW, this.canvasH)
      ctx.drawImage(this.bgImage, 0, 0, this.canvasW, this.canvasH)
      const pw = this.personNW * this.dispScale * this.scaleVal
      const ph = this.personNH * this.dispScale * this.scaleVal
      const left = this.centerX - pw / 2
      const top = this.centerY - ph / 2
      ctx.drawImage(this.personImage, Math.round(left), Math.round(top), Math.round(pw), Math.round(ph))
    },
    onMouseDown(e) {
      this.dragging = true
    },
    onMouseMove(e) {
      if (!this.dragging) return
      const rect = this.$refs.editor.getBoundingClientRect()
      this.centerX = Math.max(0, Math.min(this.canvasW, e.clientX - rect.left))
      this.centerY = Math.max(0, Math.min(this.canvasH, e.clientY - rect.top))
      this.tryRender()
    },
    onMouseUp() {
      this.dragging = false
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.05 : 0.05
      this.scaleVal = Math.max(0.1, Math.min(3, this.scaleVal + delta))
      this.tryRender()
    },
    async doComposite() {
      if (!this.canComposite) {
        this.msgError('请先选择好背景与人像')
        return
      }
      this.compositing = true
      try {
        const x = (this.centerX - this.canvasW / 2) / (this.canvasW / 2)
        const y = (this.centerY - this.canvasH / 2) / (this.canvasH / 2)
        const params = {
          person: this.personFile,
          background: this.backgroundFile || null,
          scenicId: this.selectedScenicId != null ? this.selectedScenicId : null,
          x: Number(x.toFixed(3)),
          y: Number(y.toFixed(3)),
          scale: Number(this.scaleVal.toFixed(3))
        }
        const blob = await scenicComposite(params)
        if (this.resultUrl) revokeUrl(this.resultUrl)
        this.resultUrl = blobToUrl(blob)
        this.msgSuccess('合成成功，可保存照片')
      } catch (e) {
        this.handleError(e)
      } finally {
        this.compositing = false
      }
    },
    download() {
      if (this.resultUrl) {
        downloadUrl(this.resultUrl, 'scenic_' + Date.now() + '.jpg')
      }
    },
    handleError(e) {
      const status = e && e.response && e.response.status
      if (status === 429) {
        this.msgError('今日使用次数已达上限，请明天再试')
        this.$alert('您今日的生成次数已经用完，今天请不要再尝试生成了，明天再来吧', '今日已用完', {
          type: 'warning',
          confirmButtonText: '知道了'
        })
      } else if (status === 400) {
        this.msgError('参数有误，请检查输入')
      } else {
        this.msgError('操作失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.panel-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.editor-card {
  margin-top: 0;
}

.card-header {
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  font-weight: 400;
}

.gen-layout {
  display: flex;
  gap: 28px;
  align-items: stretch;
}
.person-pane {
  flex: 0 0 38%;
}
.person-pane >>> .el-upload,
.person-pane >>> .el-upload-dragger {
  width: 100%;
}
.person-show {
  flex: 1;
}
.form-tip {
  margin: 10px 0 0;
}
.upload-box {
  text-align: center;
  margin-bottom: 12px;
}

.person-raw-preview {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

.person-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

.person-empty {
  min-height: 220px;
  border: 1px dashed #ebeef5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #bbb;
  background: #fafafa;
}

.person-empty i {
  font-size: 42px;
  margin-bottom: 10px;
}

.person-empty p {
  margin: 0;
  font-size: 13px;
}

.full-btn {
  width: 100%;
}

.bg-mode {
  margin-bottom: 14px;
}

.custom-bg {
  margin-top: 4px;
  text-align: center;
}

.tip-text {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

.scenic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
}

.scenic-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
}

.scenic-item img {
  width: 100%;
  height: auto;
  display: block;
}

.scenic-item .scenic-name {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 18px 6px 5px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0));
  color: #fff;
  font-size: 12px;
}

.scenic-item.active {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.scenic-check {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #409eff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  padding: 2px;
}

.canvas-box {
  position: relative;
  max-width: 720px;
  margin: 0 auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  cursor: move;
}

canvas {
  display: block;
  width: 100%;
}

.canvas-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 14px;
  background: rgba(250, 250, 250, 0.9);
}

.scale-row {
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 720px;
  margin: 16px auto 4px;
}

.scale-slider {
  flex: 1;
}

.actions {
  max-width: 720px;
  margin: 16px auto 0;
}

.result-box {
  margin-top: 16px;
  text-align: center;
}

.result-preview {
  max-width: 100%;
  max-height: 360px;
  border-radius: 8px;
  margin-bottom: 12px;
}
</style>
